export type CsvDelimiter = ',' | ';' | '\t' | '|';

export type CsvImportDraft = {
  fileName: string;
  headers: string[];
  rows: Array<Record<string, string>>;
  delimiter: CsvDelimiter;
};

const CANDIDATE_DELIMITERS: CsvDelimiter[] = [',', ';', '\t', '|'];

function normalizeHeader(header: string, index: number, seen: Map<string, number>): string {
  const trimmed = header.trim();
  const base = trimmed.length > 0 ? trimmed : `column_${index + 1}`;
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);
  return count === 0 ? base : `${base}_${count + 1}`;
}

function countDelimiter(line: string, delimiter: CsvDelimiter): number {
  let count = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]!;
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && char === delimiter) count += 1;
  }
  return count;
}

function detectDelimiter(text: string): CsvDelimiter {
  const firstLine =
    text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.length > 0) ?? '';

  let best: CsvDelimiter = ',';
  let bestScore = -1;
  for (const delimiter of CANDIDATE_DELIMITERS) {
    const score = countDelimiter(firstLine, delimiter);
    if (score > bestScore) {
      bestScore = score;
      best = delimiter;
    }
  }
  return best;
}

function parseDelimitedText(text: string, delimiter: CsvDelimiter): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;

  const pushField = () => {
    currentRow.push(currentField.trim());
    currentField = '';
  };

  const pushRow = () => {
    if (currentRow.length === 1 && currentRow[0] === '' && rows.length === 0) {
      currentRow = [];
      return;
    }
    if (currentRow.some((value) => value.length > 0)) {
      rows.push(currentRow);
    }
    currentRow = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        currentField += '"';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }

    if (!inQuotes && char === delimiter) {
      pushField();
      continue;
    }

    if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      pushField();
      pushRow();
      continue;
    }

    currentField += char;
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    pushField();
    pushRow();
  }

  return rows;
}

export async function parseCsvFile(
  file: File,
  opts: { maxRows?: number } = {},
): Promise<CsvImportDraft> {
  const maxRows = opts.maxRows ?? 3000;
  const raw = (await file.text()).replace(/^\uFEFF/, '');
  if (raw.trim().length === 0) {
    throw new Error('Le fichier CSV est vide.');
  }

  const delimiter = detectDelimiter(raw);
  const matrix = parseDelimitedText(raw, delimiter);
  if (matrix.length < 2) {
    throw new Error("Le CSV doit contenir une ligne d'en-tete et au moins une ligne de donnees.");
  }

  const headerSeen = new Map<string, number>();
  const headers = matrix[0]!.map((header, index) => normalizeHeader(header, index, headerSeen));
  const rows = matrix
    .slice(1)
    .map((values) => {
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = String(values[index] ?? '').trim();
      });
      return row;
    })
    .filter((row) => Object.values(row).some((value) => value.length > 0));

  if (rows.length === 0) {
    throw new Error("Le CSV ne contient aucune ligne exploitable apres l'en-tete.");
  }
  if (rows.length > maxRows) {
    throw new Error(
      `Le CSV contient ${rows.length} lignes. La limite actuelle est ${maxRows} lignes par import.`,
    );
  }

  return {
    fileName: file.name,
    headers,
    rows,
    delimiter,
  };
}
